// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState } from 'react';
import { X } from 'lucide-react';

const PAYMENT_TYPES = ['CASH', 'CARD', 'UPI', 'BANK_TRANSFER', 'WALLET', 'CHEQUE'];

export default function PaymentModeForm({ initial, cards, onSubmit, onClose }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    payment_type: initial?.payment_type || 'CASH',
    card_id: initial?.card_id || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.payment_type) {
      setError('Please fill in required fields');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = {
        ...form,
        card_id: form.card_id ? parseInt(form.card_id) : null,
      };
      await onSubmit(payload);
    } catch (err) {
      setError(err.message || 'Failed to save payment mode');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">
          {initial ? 'Edit' : 'Add'} Payment Mode
        </h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-400"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </p>
      )}

      {/* Name */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Name *
        </label>
        <input
          type="text"
          placeholder="e.g., My UPI, Office Wallet"
          value={form.name}
          onChange={set('name')}
          required
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Payment Type */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Payment Type *
        </label>
        <select
          value={form.payment_type}
          onChange={set('payment_type')}
          required
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {PAYMENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t === 'UPI' ? 'UPI' : t.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>

      {/* Card (optional, for CARD type) */}
      {form.payment_type === 'CARD' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Linked Card (optional)
          </label>
          <select
            value={form.card_id}
            onChange={set('card_id')}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">None</option>
            {cards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? 'Saving…' : 'Save Payment Mode'}
      </button>
    </form>
  );
}
